import React, { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import ChatAvatar from '../../chat/ChatAvatar';
import { useLeaderboard } from '../../../hooks/learner/useLeaderboard';
import type { LeaderboardEntry } from '../../../types/learnerPrizes';

const TABS = [
  { id: 'top7', label: 'Last 7 days' },
  { id: 'top30', label: 'Last 30 days' },
  { id: 'all', label: 'All times' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/**
 * Mirrors Figma node `I6448:67420`. The "See all" header action is
 * intentionally omitted per the user brief.
 *
 * The tab strip is rendered for visual parity with the design but only
 * the active tab reflects real data — the backend leaderboard endpoint
 * doesn't support time-window filtering yet.
 */
export default function LeaderboardCard() {
  const [activeTab, setActiveTab] = useState<TabId>('top7');
  const { entries, isLoading, isError, gamificationEnabled } = useLeaderboard(5);

  return (
    <section
      className="flex-1 lg:max-w-[465px] w-full bg-surface-elevated rounded-2xl shadow-md border border-border-primary/40 flex flex-col overflow-hidden"
      aria-label="Gamification Leaderboard"
    >
      <header className="px-6 pt-6 pb-4">
        <h3 className="text-lg font-semibold text-text-primary tracking-tight">
          Gamification Leader Board
        </h3>
      </header>

      <div className="bg-[rgba(189,188,201,0.16)] dark:bg-white/[0.04] mx-2 rounded-lg p-1 flex items-center gap-1">
        {TABS.map((tab) => {
          const isActive = tab.id === activeTab;
          const isWired = tab.id === 'top7';
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => isWired && setActiveTab(tab.id)}
              disabled={!isWired}
              className={`flex-1 h-9 px-2 rounded-full text-sm font-semibold transition-[background-color,color,transform] duration-fast ease-out-strong active:scale-[0.97] ${ isActive ? 'bg-primary-500 shadow-sm' : isWired ? 'text-text-secondary hover:bg-white/40 dark:hover:bg-white/[0.04]' : 'text-text-tertiary cursor-not-allowed opacity-60' }`}
              aria-pressed={isActive}
              title={isWired ? undefined : 'Coming soon'}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 px-6 py-6 space-y-4">
        {!gamificationEnabled ? (
          <DisabledHint />
        ) : isLoading ? (
          <LeaderboardSkeleton />
        ) : isError ? (
          <p className="text-xs text-error-500 text-center py-4">
            Couldn't load the leaderboard.
          </p>
        ) : entries.length === 0 ? (
          <p className="text-xs text-text-tertiary text-center py-4">
            No points awarded yet.
          </p>
        ) : (
          entries.map((entry) => <LeaderboardRow key={entry.learner_id} entry={entry} />)
        )}
      </div>
    </section>
  );
}

function LeaderboardRow({ entry }: { entry: LeaderboardEntry }) {
  const fullName = [entry.first_name, entry.last_name].filter(Boolean).join(' ').trim();
  return (
    <div
      className={`flex items-center gap-4 ${entry.is_me ? 'bg-primary-500/[0.06] -mx-2 px-2 py-1.5 rounded-lg' : ''}`}
    >
      <ChatAvatar src={entry.avatar_url} name={fullName} size={48} />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-semibold text-text-primary truncate">
          {fullName || '—'}
          {entry.is_me && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary-500/15 text-primary-600 dark:text-primary-350 text-[10px] font-bold uppercase tracking-wide">
              You
            </span>
          )}
        </p>
        <div className="flex items-center gap-1 text-text-secondary">
          <StarIcon className="w-4 h-4 text-warning-500" aria-hidden="true" />
          <span className="text-xs tabular-nums">
            Points {entry.balance.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

function LeaderboardSkeleton() {
  return (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-4 animate-pulse">
          <div className="w-12 h-12 rounded-xl bg-text-tertiary/15" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 rounded bg-text-tertiary/15" />
            <div className="h-3 w-16 rounded bg-text-tertiary/10" />
          </div>
        </div>
      ))}
    </>
  );
}

function DisabledHint() {
  return (
    <p className="text-xs text-text-tertiary text-center py-6">
      Gamification isn't enabled on your plan yet.
    </p>
  );
}
